import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { LayoutListItem } from './api';
import {
  RENDER_KINDS,
  type Deck,
  type DeckItem,
  type RenderKind,
  deckItemFromLayout,
  emptyDeck,
  loadDeck,
  saveDeck,
} from './deck';

interface DeckContextValue {
  deck: Deck;
  count: number;
  isInDeck: (layoutCode: string) => boolean;
  isRenderSelected: (layoutCode: string, render: RenderKind) => boolean;
  addLayout: (layout: LayoutListItem) => void;
  addLayouts: (layouts: LayoutListItem[], renderKinds?: RenderKind[]) => void;
  removeLayout: (layoutCode: string) => void;
  removeLayoutsByFamily: (familySlug: string) => void;
  toggleLayout: (layout: LayoutListItem) => void;
  toggleRenderForLayout: (layout: LayoutListItem, render: RenderKind) => void;
  setIncludePricing: (layoutCode: string, value: boolean) => void;
  toggleRender: (layoutCode: string, render: RenderKind) => void;
  setAllRenders: (layoutCode: string, kinds: RenderKind[]) => void;
  reorder: (fromIndex: number, toIndex: number) => void;
  clearDeck: () => void;
  setDeckMeta: (patch: Partial<Pick<Deck, 'name' | 'customerName'>>) => void;
  loadSavedDeck: (items: DeckItem[], customerName: string, name?: string) => void;
}

const DeckContext = createContext<DeckContextValue | null>(null);

export function DeckProvider({ children }: { children: ReactNode }) {
  // Initialize lazily from localStorage so the first render reflects persisted state.
  const [deck, setDeck] = useState<Deck>(() => loadDeck());

  useEffect(() => {
    saveDeck(deck);
  }, [deck]);

  const update = useCallback((updater: (d: Deck) => Deck) => {
    setDeck((d) => {
      const next = updater(d);
      return next === d ? d : { ...next, updatedAt: Date.now() };
    });
  }, []);

  const isInDeck = useCallback(
    (layoutCode: string) => deck.items.some((i) => i.layoutCode === layoutCode),
    [deck.items],
  );

  const isRenderSelected = useCallback(
    (layoutCode: string, render: RenderKind) => {
      const item = deck.items.find((i) => i.layoutCode === layoutCode);
      return item?.selectedRenders.includes(render) ?? false;
    },
    [deck.items],
  );

  // Toggle a single render from any context (gallery focus panel, deck page, etc).
  // If the layout isn't in the deck yet, this adds it with ONLY this render selected
  // — supports the "add an individual photo" flow.
  // If toggling off the last selected render, the layout is removed from the deck.
  const toggleRenderForLayout = useCallback(
    (layout: LayoutListItem, render: RenderKind) => {
      update((d) => {
        const idx = d.items.findIndex((i) => i.layoutCode === layout.code);
        if (idx < 0) {
          const newItem: DeckItem = {
            ...deckItemFromLayout(layout),
            selectedRenders: [render],
          };
          return { ...d, items: [...d.items, newItem] };
        }
        const current = d.items[idx]!;
        const has = current.selectedRenders.includes(render);
        const nextRenders = RENDER_KINDS.filter((k) =>
          k === render ? !has : current.selectedRenders.includes(k),
        );
        if (nextRenders.length === 0) {
          return { ...d, items: d.items.filter((_, i) => i !== idx) };
        }
        const nextItems = d.items.slice();
        nextItems[idx] = { ...current, selectedRenders: nextRenders };
        return { ...d, items: nextItems };
      });
    },
    [update],
  );

  const addLayout = useCallback((layout: LayoutListItem) => {
    update((d) => {
      if (d.items.some((i) => i.layoutCode === layout.code)) return d;
      return { ...d, items: [...d.items, deckItemFromLayout(layout)] };
    });
  }, [update]);

  const addLayouts = useCallback((layouts: LayoutListItem[], renderKinds?: RenderKind[]) => {
    update((d) => {
      const existing = new Set(d.items.map((i) => i.layoutCode));
      const newItems = layouts
        .filter((l) => !existing.has(l.code))
        .map((l) => deckItemFromLayout(l, renderKinds));
      if (newItems.length === 0) return d;
      return { ...d, items: [...d.items, ...newItems] };
    });
  }, [update]);

  const removeLayout = useCallback((layoutCode: string) => {
    update((d) => ({ ...d, items: d.items.filter((i) => i.layoutCode !== layoutCode) }));
  }, [update]);

  const removeLayoutsByFamily = useCallback((familySlug: string) => {
    update((d) => ({ ...d, items: d.items.filter((i) => i.familySlug !== familySlug) }));
  }, [update]);

  const toggleLayout = useCallback(
    (layout: LayoutListItem) => {
      if (isInDeck(layout.code)) removeLayout(layout.code);
      else addLayout(layout);
    },
    [isInDeck, addLayout, removeLayout],
  );

  const setIncludePricing = useCallback((layoutCode: string, value: boolean) => {
    update((d) => ({
      ...d,
      items: d.items.map((i) =>
        i.layoutCode === layoutCode ? { ...i, includePricing: value } : i,
      ),
    }));
  }, [update]);

  const toggleRender = useCallback((layoutCode: string, render: RenderKind) => {
    update((d) => ({
      ...d,
      items: d.items.map((i) => {
        if (i.layoutCode !== layoutCode) return i;
        const has = i.selectedRenders.includes(render);
        // Preserve RENDER_KINDS canonical order rather than the toggle order
        const next = RENDER_KINDS.filter((k) =>
          k === render ? !has : i.selectedRenders.includes(k),
        );
        return { ...i, selectedRenders: next };
      }),
    }));
  }, [update]);

  const setAllRenders = useCallback((layoutCode: string, kinds: RenderKind[]) => {
    update((d) => ({
      ...d,
      items: d.items.map((i) =>
        i.layoutCode === layoutCode ? { ...i, selectedRenders: kinds } : i,
      ),
    }));
  }, [update]);

  const reorder = useCallback((fromIndex: number, toIndex: number) => {
    update((d) => {
      if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return d;
      if (fromIndex >= d.items.length || toIndex >= d.items.length) return d;
      const next = d.items.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved!);
      return { ...d, items: next };
    });
  }, [update]);

  const clearDeck = useCallback(() => {
    setDeck(emptyDeck());
  }, []);

  const setDeckMeta = useCallback(
    (patch: Partial<Pick<Deck, 'name' | 'customerName'>>) => {
      update((d) => ({ ...d, ...patch }));
    },
    [update],
  );

  // Replace the working deck with a saved deck (the "Load & edit" flow).
  const loadSavedDeck = useCallback((items: DeckItem[], customerName: string, name = '') => {
    setDeck({
      items: items.map((i) => ({ ...i, addedAt: i.addedAt ?? Date.now() })),
      name,
      customerName,
      updatedAt: Date.now(),
    });
  }, []);

  const value = useMemo<DeckContextValue>(
    () => ({
      deck,
      // Total images (renders) selected across the deck — what the nav badge shows.
      count: deck.items.reduce((sum, i) => sum + i.selectedRenders.length, 0),
      isInDeck,
      isRenderSelected,
      addLayout,
      addLayouts,
      removeLayout,
      removeLayoutsByFamily,
      toggleLayout,
      toggleRenderForLayout,
      setIncludePricing,
      toggleRender,
      setAllRenders,
      reorder,
      clearDeck,
      setDeckMeta,
      loadSavedDeck,
    }),
    [
      deck,
      isInDeck,
      isRenderSelected,
      addLayout,
      addLayouts,
      removeLayout,
      removeLayoutsByFamily,
      toggleLayout,
      toggleRenderForLayout,
      setIncludePricing,
      toggleRender,
      setAllRenders,
      reorder,
      clearDeck,
      setDeckMeta,
      loadSavedDeck,
    ],
  );

  return <DeckContext.Provider value={value}>{children}</DeckContext.Provider>;
}

export function useDeck(): DeckContextValue {
  const ctx = useContext(DeckContext);
  if (!ctx) throw new Error('useDeck must be used inside <DeckProvider>');
  return ctx;
}
